import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { BuildPriceEntities, BuildPriceModel, BuildPricePaths } from 'client/data/models/build-price';
import { buildTransactionCountPath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { RandomModel, RandomPaths, RandomPropTypes } from 'client/data/models/random';
import { getUnifiedLeadFormDealerCountV2Path, UnifiedLeadFormModel } from 'client/data/models/unified-lead-form';
import { buildMakeModelInventoryCountPath, InventoryAggregateModel } from 'client/data/models/inventory-aggregate';
import { PageModel } from 'client/data/models/page';
import { DEALERLESS_OEM, DEALERLESS_OEM_TRIAL } from 'site-modules/shared/constants/oem';
import { BARS_PER_DEAL } from 'site-modules/shared/constants/build-price';
import { getAdsCustomTargeting, getPriceSummary } from 'site-modules/shared/utils/build-price/build-price';
import { FloodlightAd } from 'site-modules/shared/components/floodlight/floodlight-ad';
import { StickyStepHeading } from 'site-modules/build-price/components/sticky-step-heading/sticky-step-heading';
import { PriceBreakdown } from 'site-modules/build-price/components/price-breakdown/price-breakdown';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { PriceAnalysisChart } from './price-analysis-chart';
import { GetQuotesLink } from './get-quotes-link';

import './build-summary.scss';

const NO_INVOICE_STATE_NAMES = ['Louisiana'];

export function BuildSummaryUI({
  buildPriceFilter,
  optionsConfig = null,
  modelYear,
  optionsList = null,
  tmv = null,
  transactionCount = null,
  transactionsCountAdjustments = null,
  stateName = null,
  leadFormDealerCount = null,
  makeModelInventoryCount = null,
  location,
  isSummaryLeadFormSubmitted = false,
  isMobile = false,
}) {
  if (!(optionsConfig && tmv)) {
    return null;
  }

  const { makeName, modelName, makeSlug, modelSlug, year } = modelYear;
  const { optionIds, styleFilter, step } = buildPriceFilter;
  const { submodel } = styleFilter;

  const getQuotesProps = {
    buildPriceFilter,
    makeSlug,
    modelSlug,
    submodelName: submodel,
    location,
    leadFormDealerCount: isSummaryLeadFormSubmitted ? null : leadFormDealerCount,
    makeModelInventoryCount,
  };
  const hasStickyStepLink = !!(makeModelInventoryCount || getQuotesProps.leadFormDealerCount);

  const isNoInvoice = NO_INVOICE_STATE_NAMES.includes(stateName);
  const isDealerless = DEALERLESS_OEM.includes(makeSlug) || DEALERLESS_OEM_TRIAL.includes(makeSlug);
  const priceSummary = getPriceSummary({ tmv, isDealerless });

  return (
    <div className="build-summary" data-tracking-parent="step4_pricing">
      <Row className="justify-content-center">
        <Col xs={12} lg={9}>
          <StickyStepHeading
            headingText="Price summary"
            headingClassName="mb-1_5 mb-md-0"
            stickyHeadingText={`Your ${year} ${makeName} ${modelName}`}
            isMobile={isMobile}
          >
            {!isMobile && hasStickyStepLink && (
              <GetQuotesLink {...getQuotesProps} className={classnames({ 'w-100 card-btn': isMobile })} />
            )}
          </StickyStepHeading>

          <PriceAnalysisChart
            className="mb-1"
            modelYear={modelYear}
            priceSummary={priceSummary}
            transactionCount={transactionCount}
            transactionsCountAdjustments={transactionsCountAdjustments}
            isMobile={isMobile}
            isDealerless={isDealerless}
          />
          {isMobile && hasStickyStepLink && (
            <StickyStepHeading isMobile={isMobile}>
              <GetQuotesLink {...getQuotesProps} className="w-100 card-btn" />
            </StickyStepHeading>
          )}
          <PriceBreakdown
            className="mt-2_5 mb-1_5"
            optionsConfig={optionsConfig}
            tmv={tmv}
            optionsList={optionsList}
            priceSummary={priceSummary}
            optionIds={optionIds}
            isMobile={isMobile}
            isDealerless={isDealerless}
            isNoInvoice={isNoInvoice}
          />
          <div className="text-center">
            <div className="d-flex flex-column-reverse flex-md-row justify-content-center align-items-center">
              <Button
                tag="a"
                href={`${location.pathname}?defaultValue=${styleFilter.modelyearId}`}
                color="outline-primary-b"
                size="lg"
                className={classnames('build-another-link text-transform-none px-1 py-0_5', {
                  'w-100': isMobile,
                  'mx-0_5': !isMobile,
                })}
                data-tracking-id="build_price_configure_vehicle"
              >
                Build another
              </Button>
              <GetQuotesLink
                {...getQuotesProps}
                className={classnames({ 'w-100 mb-1': isMobile, 'mx-0_5': !isMobile })}
              />
            </div>
          </div>
        </Col>
      </Row>
      <FloodlightAd customTargeting={getAdsCustomTargeting(step)} />
    </div>
  );
}

BuildSummaryUI.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  buildPriceFilter: BuildPriceEntities.BuildPriceFilter.isRequired,
  modelYear: BuildPriceEntities.ModelYear.isRequired,
  optionsConfig: BuildPriceEntities.OptionsConfig,
  optionsList: PropTypes.arrayOf(BuildPriceEntities.Option),
  tmv: BuildPriceEntities.Tmv,
  transactionCount: VehicleEntities.StyleTransactionCount,
  transactionsCountAdjustments: RandomPropTypes.TransactionsCountAdjustments,
  pathname: PropTypes.string,
  stateName: PropTypes.string,
  leadFormDealerCount: PropTypes.number,
  makeModelInventoryCount: PropTypes.number,
  isSummaryLeadFormSubmitted: PropTypes.bool,
  isMobile: PropTypes.bool,
};

const mapStateToProps = state => ({
  stateName: get(state, 'visitor.location.state'),
});

export const BuildSummary = connect(mapStateToProps)(
  connectToModel(BuildSummaryUI, {
    location: bindToPath('location', PageModel),
    optionsList: bindToPath(
      ({ buildPriceFilter: { styleId } }) => BuildPricePaths.buildStyleOptionsPath(styleId),
      BuildPriceModel
    ),
    optionsConfig: bindToPath(
      ({ buildPriceFilter: { styleId, optionIds } }) =>
        BuildPricePaths.buildConfigurationWithOptionsPath(styleId, optionIds),
      BuildPriceModel
    ),
    tmv: bindToPath(
      ({ buildPriceFilter: { styleId, optionIds } }) =>
        BuildPricePaths.buildConfigurationTmvWithOptionsPath(styleId, optionIds),
      BuildPriceModel
    ),
    leadFormDealerCount: bindToPath(
      ({ modelYear: { makeSlug, modelSlug } }) =>
        getUnifiedLeadFormDealerCountV2Path({
          makeSlug,
          modelSlug,
          strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_TRIM_DST.strategy,
        }),
      UnifiedLeadFormModel
    ),
    makeModelInventoryCount: bindToPath(
      ({ modelYear: { makeSlug, modelSlug } }) =>
        buildMakeModelInventoryCountPath({ make: makeSlug, model: modelSlug }),
      InventoryAggregateModel
    ),
    transactionCount: bindToPath(
      ({ buildPriceFilter: { styleId } }) => buildTransactionCountPath(styleId),
      VehicleModel
    ),
    transactionsCountAdjustments: bindToPath(
      () => RandomPaths.getTransactionsCountAdjustments(BARS_PER_DEAL.WITH_COUNTS),
      RandomModel
    ),
  })
);
